import { z } from "zod";

/**
 * Contrat de la retouche d'image (cf. IMAGE_EDITOR.md).
 *
 * Keep in sync with
 * `inventozen-api/src/api/attachment/retouch/edit-recipe.schema.ts`.
 *
 * Copie volontaire : ce dossier est autonome (aucun import BO/API) pour
 * extraction future vers `@inventozen/image-studio`.
 *
 * Toutes les coordonnées sont normalisées (fraction de l'image SOURCE, 0..1,
 * origine coin haut-gauche) et les ajustements sont en unités neutres,
 * agnostiques du moteur de rendu — pour que le preview client (glfx/WebGL) et
 * le rendu serveur (Sharp) partent des mêmes valeurs.
 *
 * Ordre d'application (fixe): perspective → rotate → crop → contrast/density → sharpen.
 */

const unit = z.number().min(0).max(1);

/**
 * Coordonnée d'un coin de perspective. Plage étendue au-delà de [0,1] pour
 * autoriser la déformation HORS cadre. Keep in sync with `geometry.ts`
 * (PERSPECTIVE_MIN / PERSPECTIVE_MAX).
 */
const perspectiveCoord = z.number().min(-0.5).max(1.5);

/** Point normalisé [x, y] (coin de perspective, hors cadre autorisé). */
const pointSchema = z.tuple([perspectiveCoord, perspectiveCoord]);

export const editRecipeSchema = z
  .object({
    version: z.literal(1),

    // Recadrage. null = pas de recadrage.
    crop: z
      .object({
        x: unit,
        y: unit,
        w: z.number().gt(0).max(1),
        h: z.number().gt(0).max(1),
      })
      .nullable(),

    // Rotation horaire, en degrés.
    rotate: z.number().min(-360).max(360),

    // 4 coins source (haut-G, haut-D, bas-D, bas-G) à redresser. null = aucune.
    perspective: z
      .tuple([pointSchema, pointSchema, pointSchema, pointSchema])
      .nullable(),

    // Symétries (miroir). Optionnelles pour rester compatibles avec les
    // recettes déjà enregistrées avant l'ajout de ce champ.
    flipH: z.boolean().optional().default(false),
    flipV: z.boolean().optional().default(false),

    // Ajustements colorimétriques (0 = neutre).
    contrast: z.number().min(-100).max(100),
    density: z.number().min(-100).max(100),

    // Netteté (0 = aucune). Range étendue à 500 pour un accentuement très fort.
    sharpen: z.number().min(0).max(500),
  })
  .strict();

export type EditRecipe = z.infer<typeof editRecipeSchema>;

export type CropRect = NonNullable<EditRecipe["crop"]>;
export type PerspectiveCorners = NonNullable<EditRecipe["perspective"]>;
export type Point = PerspectiveCorners[number];

/** Recette neutre (aucune transformation) — utile comme valeur par défaut. */
export const NEUTRAL_RECIPE: EditRecipe = {
  version: 1,
  crop: null,
  rotate: 0,
  perspective: null,
  flipH: false,
  flipV: false,
  contrast: 0,
  density: 0,
  sharpen: 0,
};
